import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError } from '@/services/api/client'
import { getSubscriptionStatus } from '@/services/api/subscription.service'
import { createWorkspace as createWorkspaceApi } from '@/services/api/workspace.service'
import { useAuthStore } from '@/stores/auth.store'
import { usePermissionStore } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import { useWorkspaceStore } from '@/stores/workspace.store'

const createWorkspaceSchema = z.object({
  name: z.string().min(1, 'Workspace name is required'),
})

type CreateWorkspaceForm = z.infer<typeof createWorkspaceSchema>

export interface CreateWorkspaceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (workspace: { id: string; name: string }) => void
}

export function CreateWorkspaceDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateWorkspaceDialogProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { userId, workspaces, setWorkspaces, logout } = useAuthStore()
  const { setWorkspace } = useWorkspaceStore()
  const permissionStore = usePermissionStore()
  const subscriptionStore = useSubscriptionStore()

  const [isCreating, setIsCreating] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateWorkspaceForm>({
    resolver: zodResolver(createWorkspaceSchema),
  })

  useEffect(() => {
    if (open) {
      reset({ name: '' })
    }
  }, [open, reset])

  const handleCreateWorkspace = async (data: CreateWorkspaceForm) => {
    if (!userId) {
      toast.error('Unable to identify user session. Please sign in.')
      return
    }

    setIsCreating(true)
    try {
      const { loginToken } = useAuthStore.getState()
      const response = await createWorkspaceApi({
        name: data.name,
        ownerId: userId,
        // Omitted once an access token exists; the bearer token then proves identity.
        loginToken: loginToken ?? undefined,
      })

      const workspace = { id: response.id, name: response.name }
      const updatedWorkspaces = [...(workspaces ?? []), workspace]

      // Drop the previous workspace's cached data before the new token applies (as
      // useWorkspaceSwitcher does): queries not keyed by workspace would otherwise refetch the
      // old workspace's resources with the new token and fail with 403.
      await queryClient.cancelQueries()
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'workspaces',
      })

      // Apply the new session, workspace and workspace list together, then leave the current page
      // in the same render. The list must already contain the new workspace, or the switcher would
      // fall back to the first workspace while the new token is active.
      useAuthStore.getState().setAccessToken(response.accessToken)
      useAuthStore.getState().setLoginToken(null)
      setWorkspace(workspace.id, workspace.name)
      setWorkspaces(updatedWorkspaces)
      queryClient.setQueryData(['workspaces', userId], updatedWorkspaces)
      permissionStore.setIsSuperUser(response.superUser)
      permissionStore.setIsDeveloper(response.developerRole)
      // Effective permissions load via usePermission() (GET /permissions/me) for the new workspace.

      reset({ name: '' })
      onOpenChange(false)

      const createReturnUrl = sessionStorage.getItem('returnUrl')
      if (createReturnUrl) {
        sessionStorage.removeItem('returnUrl')
        navigate(createReturnUrl, { replace: true })
      } else {
        navigate('/', { replace: true })
      }

      toast.success(`Workspace "${workspace.name}" created successfully`)
      onSuccess?.(workspace)
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] })

      try {
        const subscription = await getSubscriptionStatus()
        subscriptionStore.setStatus(subscription.status)
        subscriptionStore.setCancelAtPeriodEnd(subscription.cancelAtPeriodEnd)
      } catch {
        // non-fatal
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        toast.error('Your sign-in has expired. Please sign in again.')
        sessionStorage.removeItem('returnUrl')
        logout()
        queryClient.clear()
        permissionStore.clear()
        subscriptionStore.clear()
        navigate('/login')
      } else {
        toast.error('Failed to create workspace. Please try again.')
      }
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create a new workspace</DialogTitle>
          <DialogDescription>
            Give your workspace a name.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleCreateWorkspace)} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="ws-create-name">Workspace name</Label>
            <Input
              id="ws-create-name"
              placeholder="My Company"
              {...register('name')}
              aria-invalid={!!errors.name}
            />
            {errors.name && (
              <p className="text-sm text-destructive-600">{errors.name.message}</p>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create workspace'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
